import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Download, Trash2, FileText, FileJson, Loader2, Plus, Check, AlertCircle } from 'lucide-react';

const ACCENT = '#f97316';

interface ExportJob {
  id: string;
  name: string;
  source_product: string;
  source_entity: string;
  format: 'csv' | 'json';
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_records: number;
  exported_records: number;
  file_size: number | null;
  error?: string;
  created_at: string;
  completed_at: string | null;
}

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV', icon: FileText },
  { value: 'json', label: 'JSON', icon: FileJson },
] as const;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

const statusStyles: Record<string, { bg: string; color: string; label: string }> = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  running: { bg: '#dbeafe', color: '#1e40af', label: 'Running' },
  completed: { bg: '#d1fae5', color: '#065f46', label: 'Completed' },
  failed: { bg: '#fee2e2', color: '#991b1b', label: 'Failed' },
};

export default function ExportPage() {
  const [exports, setExports] = useState<ExportJob[]>([]);
  const [entities, setEntities] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [product, setProduct] = useState('');
  const [entity, setEntity] = useState('');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');

  const loadExports = useCallback(async () => {
    try {
      const data = await api.get<{ exports: ExportJob[] }>('/exports');
      setExports(data.exports);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  const loadEntities = useCallback(async () => {
    try {
      const data = await api.get<{ entities: Record<string, string[]> }>('/export-entities');
      setEntities(data.entities);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadExports(); loadEntities(); }, [loadExports, loadEntities]);

  // Reset entity when product changes
  useEffect(() => { setEntity(''); }, [product]);

  const handleCreate = async () => {
    if (!name.trim() || !product || !entity) return;
    setCreating(true);
    try {
      await api.post('/exports', {
        name: name.trim(),
        source_product: product,
        source_entity: entity,
        format,
        filters: {},
        field_selection: [],
      });
      setShowCreate(false);
      setName('');
      setProduct('');
      setEntity('');
      setFormat('csv');
      await loadExports();
    } catch { /* empty */ }
    setCreating(false);
  };

  const handleDownload = (id: string) => {
    const apiBase = import.meta.env.VITE_API_URL || '/api';
    window.open(`${apiBase}/exports/${id}/download`, '_blank');
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/exports/${id}`);
      setConfirmDelete(null);
      await loadExports();
    } catch { /* empty */ }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Data Export</h2>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1rem', backgroundColor: ACCENT, color: '#fff',
            border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
          }}
        >
          <Plus size={16} /> New Export
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem', color: '#111827' }}>Create Export</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Export Name</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Weekly connections export"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Format</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {FORMAT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFormat(opt.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem',
                      padding: '0.5rem 1rem', border: `2px solid ${format === opt.value ? ACCENT : '#d1d5db'}`,
                      borderRadius: '0.375rem', backgroundColor: format === opt.value ? '#fff7ed' : '#fff',
                      cursor: 'pointer', fontSize: '0.875rem', fontWeight: format === opt.value ? 600 : 400,
                      color: format === opt.value ? ACCENT : '#374151',
                    }}
                  >
                    <opt.icon size={16} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Product</label>
              <select
                value={product}
                onChange={e => setProduct(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              >
                <option value="">Select product...</option>
                {Object.keys(entities).map(p => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Entity</label>
              <select
                value={entity}
                onChange={e => setEntity(e.target.value)}
                disabled={!product}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
              >
                <option value="">Select entity...</option>
                {(entities[product] || []).map(e => (
                  <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCreate(false)}
              style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !name.trim() || !product || !entity}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.5rem 1rem', backgroundColor: ACCENT, color: '#fff',
                border: 'none', borderRadius: '0.375rem', cursor: creating ? 'wait' : 'pointer',
                fontWeight: 600, fontSize: '0.875rem', opacity: creating || !name.trim() || !product || !entity ? 0.5 : 1,
              }}
            >
              {creating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={16} />}
              {creating ? 'Exporting...' : 'Start Export'}
            </button>
          </div>
        </div>
      )}

      {/* Export History */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loader2 size={32} color="#9ca3af" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : exports.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <Download size={48} color="#d1d5db" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>No Exports Yet</h3>
          <p style={{ color: '#6b7280', maxWidth: 400, margin: '0 auto' }}>
            Create your first export to download Bridge data as CSV or JSON.
          </p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Name</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Source</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Format</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Records</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Size</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Created</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exports.map(exp => {
                const st = statusStyles[exp.status] || statusStyles.pending;
                return (
                  <tr key={exp.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: '#111827' }}>{exp.name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                      {exp.source_product}.{exp.source_entity}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#6b7280' }}>
                        {exp.format === 'csv' ? <FileText size={14} /> : <FileJson size={14} />}
                        {exp.format.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: st.bg, color: st.color,
                      }}>
                        {exp.status === 'completed' && <Check size={12} />}
                        {exp.status === 'failed' && <AlertCircle size={12} />}
                        {exp.status === 'running' && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                      {exp.exported_records.toLocaleString()}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>
                      {exp.file_size ? formatBytes(exp.file_size) : '--'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                      {formatDate(exp.created_at)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        {exp.status === 'completed' && (
                          <button
                            onClick={() => handleDownload(exp.id)}
                            title="Download"
                            style={{
                              padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem',
                              backgroundColor: '#fff', cursor: 'pointer', color: '#059669',
                            }}
                          >
                            <Download size={14} />
                          </button>
                        )}
                        {confirmDelete === exp.id ? (
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              onClick={() => handleDelete(exp.id)}
                              style={{ padding: '0.375rem', border: '1px solid #ef4444', borderRadius: '0.25rem', backgroundColor: '#fee2e2', cursor: 'pointer', color: '#dc2626' }}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              style={{ padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', backgroundColor: '#fff', cursor: 'pointer', color: '#6b7280' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDelete(exp.id)}
                            title="Delete"
                            style={{
                              padding: '0.375rem', border: '1px solid #d1d5db', borderRadius: '0.25rem',
                              backgroundColor: '#fff', cursor: 'pointer', color: '#dc2626',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
